/* FAQ — preguntas frecuentes con acordeón.
   - Solo un item abierto a la vez (controlado en JSX).
   - Animación con grid-template-rows: 0fr → 1fr (alternativa moderna y
     suave a max-height; el resultado visual es el "max-height animado"
     que pide el spec, sin saltos).
   - Respuestas siempre en el HTML (no lazy): SEO friendly.
   - Tags "Usuario" / "Profesional" en el extremo derecho para permitir
     escaneo por audiencia.
   - Reveal del bloque entero al entrar al viewport (sin stagger). */

.section {
  background: var(--sand-100);
  padding-block: var(--space-20);
}

@media (min-width: 768px) {
  .section {
    padding-block: var(--space-24);
  }
}

@media (min-width: 1024px) {
  .section {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

/* ─── Lista ─── */

.list {
  max-width: var(--container-narrow);
  margin-inline: auto;
  margin-top: var(--space-10);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);

  /* Reveal unitario, sin stagger por item. */
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 700ms var(--ease-decelerate),
    transform 700ms var(--ease-decelerate);
  will-change: opacity, transform;
}

.revealed.list {
  opacity: 1;
  transform: translateY(0);
}

@media (min-width: 768px) {
  .list {
    margin-top: var(--space-12);
  }
}

/* ─── Item ─── */

.item {
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-xl);
  overflow: hidden;
  transition:
    border-color var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);
}

.item:hover {
  border-color: var(--color-border);
}

.itemOpen {
  border-color: var(--color-border);
  box-shadow: var(--shadow-sm);
}

.itemHeading {
  margin: 0;
  font: inherit;
}

/* ─── Trigger ─── */

.trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  background: transparent;
  border: 0;
  padding: var(--space-4) var(--space-5);
  text-align: left;
  cursor: pointer;
  color: var(--color-text);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-base);
  line-height: 1.4;
  letter-spacing: -0.005em;
}

@media (min-width: 768px) {
  .trigger {
    padding: var(--space-5) var(--space-6);
    font-size: var(--text-lg);
  }
}

.trigger:focus-visible {
  outline: 2px solid var(--color-border-focus);
  outline-offset: -2px;
}

.question {
  flex: 1;
}

.right {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .right {
    gap: var(--space-3);
  }
}

/* ─── Tag de audiencia ─── */

.tag {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 3px var(--space-2);
  border-radius: var(--radius-full);
  background: var(--blue-50);
  color: var(--blue-700);
  white-space: nowrap;
}

.tag[data-audience='professional'] {
  background: var(--jade-50);
  color: var(--jade-700);
}

@media (min-width: 768px) {
  .tag {
    font-size: var(--text-xs);
  }
}

/* ─── Chevron ─── */

.chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  transition:
    transform 250ms ease-in-out,
    color var(--duration-base) var(--ease-standard);
}

.itemOpen .chevron {
  transform: rotate(180deg);
  color: var(--blue-700);
}

/* ─── Panel (respuesta) ─── */

/* grid-template-rows: 0fr → 1fr da una animación suave de altura sin
   conocer el alto del contenido. El nodo interno usa overflow: hidden
   para que el clip funcione durante la transición. */
.panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 280ms ease-in-out;
}

.itemOpen .panel {
  grid-template-rows: 1fr;
}

.panelInner {
  overflow: hidden;
  min-height: 0;
}

.panelInner p {
  font-family: var(--font-body);
  font-size: var(--text-base);
  line-height: 1.65;
  color: var(--color-text-muted);
  margin: 0;
  padding: 0 var(--space-5) var(--space-5);
  max-width: 68ch;
}

@media (min-width: 768px) {
  .panelInner p {
    padding: 0 var(--space-6) var(--space-6);
  }
}

/* ─── Accesibilidad: respeta prefers-reduced-motion ─── */

@media (prefers-reduced-motion: reduce) {
  .list,
  .item,
  .chevron,
  .panel {
    transition: none;
  }
  .list {
    opacity: 1;
    transform: none;
  }
}
